/**
 * AppDetail — /apps/:configKey
 *
 * The per-app page: shows header, the scripts scoped to this app, the clients
 * using this app (from credential_vault), and recent runs. Clicking Edit on a
 * script navigates to the existing /scripts/:id/edit route. Clicking a run
 * goes to /scripts/:id/runs/:execId.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
  Paper,
  Tabs,
  Tab,
  Breadcrumbs,
  Link as MuiLink,
  Avatar,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Badge,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Code as CodeIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import appRegistryService from '../services/api/appRegistryService';
import extensionScriptsService from '../services/api/extensionScriptsService';
import { ExecutorPlayButton, ExecutorModeChip } from '../components/ExecutorModeControls';

const STATUS_COLOR = {
  passed: 'success',
  completed: 'success',
  failed: 'error',
  error: 'error',
  running: 'warning',
  partial: 'warning',
  never_run: 'default',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const AppDetail = () => {
  const { configKey } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const [appMeta, setAppMeta] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [clients, setClients] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      appRegistryService.getOverview(),
      appRegistryService.getAppScripts(configKey).catch((e) => {
        if (e?.response?.status === 404) return { data: [] };
        throw e;
      }),
      appRegistryService.getAppClients(configKey).catch((e) => {
        if (e?.response?.status === 404) return { data: [] };
        throw e;
      }),
      appRegistryService.getAppRecentRuns(configKey, 20).catch((e) => {
        if (e?.response?.status === 404) return { data: [] };
        throw e;
      }),
    ])
      .then(([overviewRes, scriptsRes, clientsRes, runsRes]) => {
        if (cancelled) return;
        const overview = Array.isArray(overviewRes.data) ? overviewRes.data : [];
        const meta = overview.find((a) => a.configKey === configKey) || null;
        setAppMeta(meta);
        setScripts(scriptsRes.data || []);
        setClients(clientsRes.data || []);
        setRuns(runsRes.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.message || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [configKey]);

  const latestScriptVersions = useMemo(() => {
    const m = new Map();
    for (const s of scripts) m.set(s.id, s.version);
    return m;
  }, [scripts]);

  const handleRunHeadless = async (scriptId, mode = 'playwright') => {
    try {
      await extensionScriptsService.runByExecutor(scriptId, mode);
    } catch (err) {
      // Best-effort — errors surface in the backend logs / run history
      console.warn('Headless run failed:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Failed to load app: {error}</Alert>
      </Container>
    );
  }

  if (!appMeta) {
    return (
      <Container maxWidth="xl">
        <Alert severity="warning">App '{configKey}' not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/apps" underline="hover" color="inherit">
          Apps
        </MuiLink>
        <Typography color="text.primary">{appMeta.displayName || appMeta.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        {appMeta.logo ? (
          <Avatar src={appMeta.logo} alt={appMeta.name} variant="rounded" sx={{ width: 64, height: 64, bgcolor: 'grey.100' }} />
        ) : (
          <Avatar variant="rounded" sx={{ width: 64, height: 64, bgcolor: 'primary.light' }}>
            <AppsIcon />
          </Avatar>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>
            {appMeta.displayName || appMeta.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Chip label={appMeta.configKey} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
            {appMeta.kind && <Chip label={appMeta.kind} size="small" color="primary" variant="outlined" />}
            {(appMeta.tags || []).map((t) => (
              <Chip key={t} label={t} size="small" variant="outlined" />
            ))}
            {appMeta.linkAddress && (
              <Chip
                icon={<OpenInNewIcon />}
                label="Website"
                size="small"
                component="a"
                href={appMeta.linkAddress}
                target="_blank"
                rel="noreferrer"
                clickable
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
        <Stack spacing={0.5} alignItems="flex-end">
          <Typography variant="body2" color="text.secondary">
            <strong>{appMeta.clientCount}</strong> clients · <strong>{appMeta.scriptCount}</strong> scripts
          </Typography>
        </Stack>
      </Stack>

      {appMeta.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {appMeta.description}
        </Typography>
      )}

      <Paper elevation={1}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<CodeIcon />} iconPosition="start" label={`Scripts (${scripts.length})`} />
          <Tab icon={<BusinessIcon />} iconPosition="start" label={`Clients (${clients.length})`} />
          <Tab icon={<HistoryIcon />} iconPosition="start" label={`Recent runs (${runs.length})`} />
        </Tabs>

        {/* Scripts tab */}
        <TabPanel value={tab} index={0}>
          {scripts.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">No scripts configured for this app.</Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell align="center"><strong>Version</strong></TableCell>
                    <TableCell align="center"><strong>Steps</strong></TableCell>
                    <TableCell><strong>Last status</strong></TableCell>
                    <TableCell><strong>Last run</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scripts.map((s) => {
                    const status = s.lastStatus || 'never_run';
                    const color = STATUS_COLOR[status] || 'default';
                    return (
                      <TableRow
                        key={s.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/scripts/${s.id}/edit`)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                          {s.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 420 }} noWrap>
                              {s.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(s.scriptType || s.type || 'custom').replace(/_/g, '-')}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Badge color="info" badgeContent={`v${s.version}`} sx={{ '& .MuiBadge-badge': { position: 'relative', transform: 'none', fontSize: '0.7rem' } }}>
                            <span />
                          </Badge>
                        </TableCell>
                        <TableCell align="center">{s.stepCount || 0}</TableCell>
                        <TableCell>
                          <Chip label={status} size="small" color={color} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(s.lastRunAt)}</Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => navigate(`/scripts/${s.id}/edit`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <ExecutorPlayButton
                            tooltip="Run Headless (chevron picks Playwright vs v5 ext)"
                            onRun={(mode) => handleRunHeadless(s.id, mode)}
                          />
                          <Tooltip title="Watch Mode">
                            <IconButton size="small" color="info" onClick={() => navigate(`/scripts/${s.id}/edit?watch=true`)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Clients tab */}
        <TabPanel value={tab} index={1}>
          {clients.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">No credential_vault entries exist yet for this app.</Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Client</strong></TableCell>
                    <TableCell align="right"><strong>Credentials</strong></TableCell>
                    <TableCell><strong>Environment</strong></TableCell>
                    <TableCell><strong>Last run status</strong></TableCell>
                    <TableCell><strong>Last run</strong></TableCell>
                    <TableCell><strong>Script versions in use</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((c) => {
                    const numericId = c.numericClientId ?? null;
                    const canLink = Boolean(numericId);
                    const clientLabel = c.clientName || c.clientTag || c.clientId;
                    const seed = c.clientTag || c.clientId || clientLabel || '';
                    return (
                      <TableRow
                        key={c.clientId}
                        hover
                        sx={{ cursor: canLink ? 'pointer' : 'default' }}
                        onClick={canLink ? () => navigate(`/clients/${numericId}`) : undefined}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <ClientAvatar
                              name={clientLabel}
                              seed={seed}
                              logoUrl={c.logoUrl}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {clientLabel}
                              </Typography>
                              {c.clientTag && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontFamily="monospace"
                                  display="block"
                                >
                                  {c.clientTag}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{c.credentialCount}</TableCell>
                        <TableCell>
                          <Chip label={c.environment || '—'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {c.lastRunStatus ? (
                            <Chip label={c.lastRunStatus} size="small" color={STATUS_COLOR[c.lastRunStatus] || 'default'} variant="outlined" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(c.lastRunAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          {/* Per-client version assignment doesn't exist yet — show latest versions across all app scripts */}
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {scripts.slice(0, 3).map((s) => (
                              <Chip key={s.id} label={`${(s.scriptType || s.type || '').replace(/_/g, '-')} v${latestScriptVersions.get(s.id) || s.version}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                            ))}
                            {scripts.length > 3 && (
                              <Chip label={`+${scripts.length - 3}`} size="small" sx={{ fontSize: '0.68rem' }} />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Recent runs tab */}
        <TabPanel value={tab} index={2}>
          {runs.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">No run history recorded yet for this app.</Alert>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Started</strong></TableCell>
                    <TableCell><strong>Script</strong></TableCell>
                    <TableCell><strong>Client</strong></TableCell>
                    <TableCell><strong>Trigger</strong></TableCell>
                    <TableCell><strong>Engine</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Steps</strong></TableCell>
                    <TableCell align="right"><strong>Duration</strong></TableCell>
                    <TableCell align="right"><strong></strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="caption">{formatDate(r.startedAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <MuiLink component={Link} to={`/scripts/${r.scriptId}/edit`} underline="hover">
                          {r.scriptName}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{r.clientName || r.clientId || '—'}</TableCell>
                      <TableCell>
                        <Chip label={r.trigger} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <ExecutorModeChip exec={r} />
                      </TableCell>
                      <TableCell>
                        <Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">{r.stepsCompleted || 0}/{r.stepsTotal || 0}</TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">
                          {r.durationMs ? `${Math.round(r.durationMs / 100) / 10}s` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/scripts/${r.scriptId}/runs/${r.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

/**
 * ClientAvatar — renders the client's white-label logo if one is set, else a
 * deterministic initials-on-color avatar. The hue is seeded by the client tag
 * so the same tag always renders the same color.
 */
function hueFromString(s) {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

function initialsFromName(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ClientAvatar = ({ name, seed, logoUrl, size = 32 }) => {
  const [broken, setBroken] = React.useState(false);
  const initials = initialsFromName(name);
  const hue = hueFromString(seed || name || '');
  const bg = `hsl(${hue}, 55%, 55%)`;
  if (logoUrl && !broken) {
    return (
      <Avatar
        src={logoUrl}
        alt={name}
        variant="rounded"
        sx={{ width: size, height: size, bgcolor: 'grey.100' }}
        imgProps={{ onError: () => setBroken(true) }}
      >
        {initials}
      </Avatar>
    );
  }
  return (
    <Avatar
      variant="rounded"
      sx={{
        width: size,
        height: size,
        bgcolor: bg,
        color: '#fff',
        fontSize: size < 28 ? '0.7rem' : '0.85rem',
        fontWeight: 700,
      }}
    >
      {initials}
    </Avatar>
  );
};

export default AppDetail;
